import "@xyflow/react/dist/style.css";

import {
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  type IsValidConnection,
  type OnConnect,
  type OnEdgesChange,
  type OnNodesChange,
} from "@xyflow/react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useColorScheme } from "@mui/material/styles";
import ConditionEdge from "./ConditionEdge.tsx";
import DataSourceEdge from "./DataSourceEdge.tsx";
import DataSourceNode from "./DataSourceNode.tsx";
import StepNode, { KIND_ICONS } from "./StepNode.tsx";
import type { ActionKind, EditorEdge, EditorNode } from "./types.ts";

// One entry of the "Add step" palette: a bespoke step kind, or a step type
// only the backend knows (`kind: "custom"` with its `stepType`).
export interface PaletteItem {
  key: string;
  kind: ActionKind;
  stepType?: string;
  label: string;
  description: string;
}

// Module-level so xyflow doesn't see new types on every render.
const nodeTypes = { step: StepNode, data_source: DataSourceNode };
const edgeTypes = { condition: ConditionEdge, data_source: DataSourceEdge };
const DELETE_KEYS = ["Backspace", "Delete"];
// Data sources are not steps: fade them in the minimap. "" leaves a step on the default fill.
const minimapColor = (n: EditorNode) => (n.type === "data_source" ? "var(--mui-palette-action-disabledBackground)" : "");

interface Props {
  nodes: EditorNode[];
  edges: EditorEdge[];
  onNodesChange: OnNodesChange<EditorNode>;
  onEdgesChange: OnEdgesChange<EditorEdge>;
  onConnect: OnConnect;
  isValidConnection: IsValidConnection<EditorEdge>;
  onNodeClick: (node: EditorNode) => void;
  onPaneClick: () => void;
  onAddStep: (kind: ActionKind, stepType?: string) => void;
  // Step types the palette offers (the API's, in display order).
  paletteItems: PaletteItem[];
  // The step a new one would be chained under, for the palette hint.
  addTargetLabel: string;
  // Explain the dashed pills once the canvas has an agent to hang them on.
  showLegend: boolean;
  readOnly: boolean;
}

export default function WorkflowCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  isValidConnection,
  onNodeClick,
  onPaneClick,
  onAddStep,
  paletteItems,
  addTargetLabel,
  showLegend,
  readOnly,
}: Props) {
  const { mode, systemMode } = useColorScheme();
  const resolved = mode === "system" ? systemMode : mode;

  return (
    <Box
      sx={{
        flexGrow: 1,
        minWidth: 0,
        minHeight: 0,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        "& .react-flow__attribution": { display: "none" },
      }}
    >
      <ReactFlow<EditorNode, EditorEdge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        onNodeClick={(_, node) => onNodeClick(node)}
        onPaneClick={onPaneClick}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        deleteKeyCode={readOnly ? null : DELETE_KEYS}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        minZoom={0.3}
        maxZoom={1.5}
        colorMode={resolved === "dark" ? "dark" : "light"}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable nodeStrokeWidth={2} nodeColor={minimapColor} />
        {!readOnly && paletteItems.length > 0 && (
          <Panel position="top-left">
            <Paper variant="outlined" sx={{ p: 1, display: "flex", flexDirection: "column", gap: 0.5, minWidth: 190 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", px: 0.5 }}>
                Add step after “{addTargetLabel}”
              </Typography>
              {paletteItems.map((item) => (
                <Tooltip key={item.key} title={item.description} placement="right" describeChild>
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    startIcon={KIND_ICONS[item.kind]}
                    onClick={() => onAddStep(item.kind, item.stepType)}
                    sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 400 }}
                  >
                    {item.label}
                  </Button>
                </Tooltip>
              ))}
            </Paper>
          </Panel>
        )}
        {showLegend && (
          // bottom-left is where <Controls> sits; this is the next slot over.
          <Panel position="bottom-center">
            <Paper
              variant="outlined"
              sx={{ px: 1.25, py: 0.5, display: "flex", alignItems: "center", gap: 1.5, fontSize: "0.7rem" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 10,
                    borderRadius: "2px",
                    border: "1px solid",
                    borderColor: "text.secondary",
                    bgcolor: "background.paper",
                  }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Step
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 10,
                    borderRadius: "999px",
                    border: "1px dashed",
                    borderColor: "text.disabled",
                    bgcolor: "background.default",
                  }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Data source — not a step; click to set limits
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 0,
                    borderTop: "2px dashed",
                    borderColor: "warning.main",
                  }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Waits for human approval
                </Typography>
              </Box>
            </Paper>
          </Panel>
        )}
      </ReactFlow>
    </Box>
  );
}
